import { Staff } from "@/graphql/types";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "../common/modal/modal";
import { InputLabel, TextField } from "@mui/material";
import DeleteIcon from "../common/commonIcon/deleteIcon";
import Button from "../common/button/button";
import LoadingIcon from "../common/loadingIcon";
import ResultBar from "../common/resultBar";
import { SetStaffsInput } from ".";
import { Device } from "@/lib/ts/device";
import Parallelogram from "../common/commonIcon/parallelogram";

type Props = {
  existingStaffs: Staff[];
  onClose: () => void;
  stageId?: number;
  productionId?: number;
  save: (input: SetStaffsInput) => Promise<void>;
  device?: Device | undefined;
};

const regex = /^[^!@#$%^&*()_+{}/\[\]:;<>,.?~=-]+$/;
const phoneticRegex = /^[\u3041-\u3096ー]+$/;

// スタッフ登録可能数上限
const MAX_STAFF = 100;
const MAX_NAME_LENGTH = 20;

const SetupStaffs = (props: Props) => {
  const [inputStaffsText, setInputStaffsText] = useState("");
  const [invalidLengthList, setInvalidLengthList] = useState([] as number[]);
  const [regexInvalidList, setRegexInvalidList] = useState([] as number[]);

  const [inputPhonetics, setInputPhonetics] = useState(
    [] as (string | undefined)[],
  );

  const [existingStaffs, setExistingStaffs] = useState(props.existingStaffs);
  const [addedStaffs, setAddedStaffs] = useState([] as Staff[]);

  const [showAnotherModal, setShowAnotherModal] = useState(
    undefined as undefined | "setPhonetic" | "updateExisting" | "updateAdded",
  );

  const [eventTarget, setEventTarget] = useState(undefined as undefined | any);

  const [saveError, setSaveError] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [deletedStaffIds, setDeletedStaffIds] = useState([] as number[]);

  const [updatedStaffIndex, setUpdatedStaffIndex] = useState(
    undefined as undefined | number,
  );
  const [updatedStaff, setUpdatedStaff] = useState(
    undefined as Staff | undefined,
  );
  const [updatedStaffError, setUpdatedStaffError] = useState({
    name: false,
    phonetic: false,
  });

  const [updatedStaffsIndexes, setUpdatedStaffsIndexes] = useState(
    [] as number[],
  );

  const updatedStaffs = useMemo(
    () =>
      existingStaffs.filter(
        (staff, index) => updatedStaffsIndexes.indexOf(index) !== -1,
      ),
    [existingStaffs, updatedStaffsIndexes],
  );

  /*
  const [setStaffsOfProduction] = GraphQLClient.useLazyQuery(
    SetStaffsOfProduction,
    {
      onCompleted: () => {
        setLoading(false)
        setSaveSuccess(true)
      },
      onError: () => {
        setLoading(false)
        setSaveError(true);
      },
    },
  
  */

  const save = useCallback(async () => {
    /*
    await setStaffsOfProduction({
        variables: {
            input: {
                addOrUpdateStaffsInput: addedStaffs,
                deleteStaffsInput: deletedStaffIds,
            }
        }
    })
    */
    const input = {
      addOrUpdateStaffsInput: [...addedStaffs, ...updatedStaffs],
      deleteStaffsInput: deletedStaffIds,
    };
    await props.save(input);
  }, [addedStaffs, deletedStaffIds, props, updatedStaffs]);

  const inputStaffs = useMemo(() => {
    const splitter = "\n";
    const separatedText = inputStaffsText.split(splitter);

    console.log(separatedText);
    return separatedText;
  }, [inputStaffsText]);

  // 入力エラー検知
  const validation: { [key: string]: boolean } = useMemo(() => {
    const validationResult = {
      length: false,
      empty: false,
      eachLength: false,
      regex: false,
    };

    // 100人越え確認
    if (inputStaffs.length > MAX_STAFF) {
      validationResult.length = true;
      return validationResult;
    }

    // 空白行確認
    if (inputStaffs.some((text) => !text)) {
      validationResult.empty = true;
      return validationResult;
    }

    // 文字数
    const invalidLengthList: number[] = [];
    if (
      inputStaffs.filter((text, index) => {
        const invalid = text.length > MAX_NAME_LENGTH;
        if (invalid) {
          invalidLengthList.push(index + 1);
        }
        return invalid;
      }).length
    ) {
      validationResult.eachLength = true;
    }
    setInvalidLengthList(invalidLengthList);

    // 入力規則
    const regexInvalidList: number[] = [];
    if (
      inputStaffs.filter((text, index) => {
        const match = text.match(regex);
        const invalid = !match || !match.length;
        if (invalid) {
          regexInvalidList.push(index + 1);
        }
        return invalid;
      }).length
    ) {
      validationResult.regex = true;
    }
    setRegexInvalidList(regexInvalidList);

    return validationResult;
  }, [inputStaffs]);

  const addButtonActive = useMemo(() => {
    let result = true;
    Object.keys(validation).forEach((key) => {
      result = result && !validation[key];
    });
    return result;
  }, [validation]);

  const errorMessages = useMemo(() => {
    const message: JSX.Element[] = [];
    if (validation.length) {
      message.push(
        <div className="error-message">スタッフは最大{MAX_STAFF}人です。</div>,
      );
    } else {
      message.push(
        <div className="helper-message">スタッフは最大{MAX_STAFF}人です。</div>,
      );
    }
    if (validation.eachLength) {
      message.push(
        <div className="error-message">
          <div>スタッフ名は最長で{MAX_NAME_LENGTH}文字です。</div>
          <div>{invalidLengthList.join(", ")}行目が字数を超過しています。</div>
        </div>,
      );
    } else {
      message.push(
        <div className="helper-message">
          スタッフ名は最長で{MAX_NAME_LENGTH}文字です。
        </div>,
      );
    }
    if (validation.empty) {
      message.push(<div className="error-message">空白の行があります。</div>);
    }

    if (validation.regex) {
      message.push(
        <div className="error-message">
          {regexInvalidList.join(", ")}行目に使用できない文字が含まれています。
        </div>,
      );
    }

    return message;
  }, [
    invalidLengthList,
    regexInvalidList,
    validation.eachLength,
    validation.empty,
    validation.length,
    validation.regex,
  ]);

  const onPhoneticChange = useCallback(
    (phonetic: string, index: number) => {
      const inputPhoneticsCopy: (string | undefined)[] = [...inputPhonetics];
      if (!inputPhonetics.length) {
        inputStaffs.forEach(() => {
          inputPhoneticsCopy.push(undefined);
        });
      }
      inputPhoneticsCopy[index] = phonetic;
      setInputPhonetics(inputPhoneticsCopy);
    },
    [inputPhonetics, inputStaffs],
  );

  const addStaffs = useCallback(() => {
    const newStaffs = inputStaffs.map(
      (staffName, index) =>
        ({
          name: staffName,
          phonetic: inputPhonetics[index],
          stageId: props.stageId || -1,
          productionId: props.productionId || -1,
        }) as Staff,
    );
    const addedStaffsCopy = [...addedStaffs, ...newStaffs];

    setAddedStaffs(addedStaffsCopy);

    // 入力リセット
    eventTarget.value = "";
    console.log(eventTarget);

    setInputPhonetics([]);
    setInputStaffsText("");

    setShowAnotherModal(undefined);
  }, [
    addedStaffs,
    eventTarget,
    inputPhonetics,
    inputStaffs,
    props.productionId,
    props.stageId,
  ]);

  const phoneticValidation = useCallback(
    (index: number) => {
      const phonetic = inputPhonetics[index];
      if (!phonetic) {
        return true;
      }
      const match = phonetic.match(phoneticRegex);
      return !(match && match.length);
    },
    [inputPhonetics],
  );

  const phoneticAddButtonActive = useMemo(() => {
    let result = true;
    if (inputPhonetics.length === 0) {
      return false;
    }
    inputPhonetics.forEach((value, index) => {
      result = result && !phoneticValidation(index);
    });
    return result;
  }, [inputPhonetics, phoneticValidation]);

  const setPhoneticModal = useMemo(() => {
    const phoneticInputs = inputStaffs.map((staffName, index) => (
      <div key={`phonetic-input-${index}`} className="phonetic-input-wrapper">
        <div className="phonetic-staff-name">{staffName}</div>
        <div>
          <TextField
            id="staff-names-input-unique"
            multiline={false}
            onChange={(event) => {
              onPhoneticChange(event.target.value, index);
            }}
            error={phoneticValidation(index)}
            helperText={
              phoneticValidation(index) ? "ひらがなのみ使用できます" : ""
            }
          />
        </div>
      </div>
    ));

    return (
      <Modal
        title="ふりがな設定"
        onClose={() => {
          setInputPhonetics([]);
          setShowAnotherModal(undefined);
        }}
        maxHeight={600}
      >
        <>
          {phoneticInputs}
          <Button
            buttonType="forward"
            onClick={addStaffs}
            label="追加"
            disabled={!phoneticAddButtonActive}
          />
        </>
      </Modal>
    );
  }, [
    addStaffs,
    inputStaffs,
    onPhoneticChange,
    phoneticAddButtonActive,
    phoneticValidation,
  ]);

  const deleteExistingStaff = useCallback(
    (index: number) => {
      const existingStaffsCopy = [...existingStaffs];
      const id = existingStaffsCopy[index].id;
      if (id) {
        setDeletedStaffIds([...deletedStaffIds, id]);
      }

      existingStaffsCopy.splice(index, 1);
      setExistingStaffs(existingStaffsCopy);

      const targetIndex = updatedStaffsIndexes.indexOf(index);
      if (targetIndex !== -1) {
        updatedStaffsIndexes.splice(targetIndex, 1);
      }
    },
    [deletedStaffIds, existingStaffs, updatedStaffsIndexes],
  );

  const deleteAddedStaff = useCallback(
    (index: number) => {
      const addedStaffsCopy = [...addedStaffs];
      addedStaffsCopy.splice(index, 1);
      setAddedStaffs(addedStaffsCopy);
    },
    [addedStaffs],
  );

  const leftBox = useMemo(() => {
    return (
      <div className="left-box">
        <div className="text-area">
          <TextField
            multiline
            rows={props.device === "PC" ? 18 : 10}
            onChange={(event) => {
              setEventTarget(event.target);
              setInputStaffsText(event.target.value);
            }}
            fullWidth={props.device === "PC"}
          />
        </div>
      </div>
    );
  }, [props.device]);

  const rightBox = useMemo(() => {
    const existingStaffsComponent = existingStaffs.map((staff, index) => (
      <div className="staff-parallelogram">
        <Parallelogram 
          key={`existing-staff-${index}`}
          bodyColor="black"
          shadowColor="red"
          height={props.device === "PC" ? 70 : 40}
          width={props.device === "PC" ? 380 : 150}
          padding={props.device === "PC" ? [10, 40, 0, 40] : [0, 0, 0, 12]}
        >
          <>
          <div className="existing-staff">
            <div
              className="existing-staff-property"
              onClick={() => {
                setUpdatedStaffIndex(index);
                setUpdatedStaff(staff);
                setShowAnotherModal("updateExisting");
              }}
            >
              <div className="existing-staff-name">{staff.name}</div>
              <div className="existing-staff-phonetic">{staff.phonetic}</div>
            </div>
            <div className="delete-icon">
              <DeleteIcon
                height={props.device === "PC" ? 16 : 12}
                width={props.device === "PC" ? 16 : 12}
                onClick={() => {
                  deleteExistingStaff(index);
                }}
                color="white"
              />
            </div>
          </div>
          </>
        </Parallelogram>
      </div>
    ));

    const addedStaffsComponent = addedStaffs.map((staff, index) => (
      <div className="staff-parallelogram">
        <Parallelogram 
          key={`added-staff-${index}`}
          bodyColor="red"
          shadowColor="black"
          height={props.device === "PC" ? 70 : 40}
          width={props.device === "PC" ? 380 : 150}
          padding={props.device === "PC" ? [10, 40, 0, 40] : [0, 0, 0, 0]}
        >
          <div className="added-staff">
            <div
              className="added-staff-property"
              onClick={() => {
                setUpdatedStaffIndex(index);
                setUpdatedStaff(staff);
                setShowAnotherModal("updateAdded");
              }}
            >
              <div className="added-staff-name">{staff.name}</div>
              <div className="added-staff-phonetic">{staff.phonetic}</div>
            </div>
            <div className="delete-icon">
              <DeleteIcon
                height={16}
                width={16}
                onClick={() => {
                  deleteAddedStaff(index);
                }}
                color="white"
              />
            </div>
          </div>
        </Parallelogram>
      </div>
    ));

    const mergedStaffsComponent = [
      ...existingStaffsComponent,
      ...addedStaffsComponent,
    ];

    return (
      <div className="right-box">
        <div className="staffs-display">{mergedStaffsComponent}</div>
      </div>
    );
  }, [addedStaffs, deleteAddedStaff, deleteExistingStaff, existingStaffs, props.device]);

  const onUpdated = useCallback(
    (property: "name" | "phonetic", value: string) => {
      if (!updatedStaff) {
        return;
      }
      const staffCopy: Staff = { ...updatedStaff };
      const updatedStaffErrorCopy = { ...updatedStaffError };
      if (property === "name") {
        const match = staffCopy.name.match(regex);
        if (!match || !match.length) {
          updatedStaffErrorCopy.name = true;
          setUpdatedStaffError(updatedStaffErrorCopy);
          return;
        }
        staffCopy.name = value;
      }
      if (property === "phonetic") {
        const match = staffCopy.phonetic.match(regex);
        if (!match || !match.length) {
          updatedStaffErrorCopy.phonetic = true;
          setUpdatedStaffError(updatedStaffErrorCopy);
          return;
        }
        staffCopy.phonetic = value;
      }
      setUpdatedStaff(staffCopy);
    },
    [updatedStaff, updatedStaffError],
  );

  const decideUpdate = useCallback(() => {
    console.log(updatedStaffIndex, updatedStaff);
    if (updatedStaffIndex === undefined || updatedStaff === undefined) {
      return;
    }
    if (showAnotherModal === "updateAdded") {
      const addedStaffsCopy = [...addedStaffs];
      addedStaffsCopy[updatedStaffIndex] = { ...updatedStaff };
      setAddedStaffs(addedStaffsCopy);
    }
    if (showAnotherModal === "updateExisting") {
      const existingStaffsCopy = [...existingStaffs];
      existingStaffsCopy[updatedStaffIndex] = { ...updatedStaff };
      setExistingStaffs(existingStaffsCopy);

      // サーバに送信するデータとしてインデックス追加
      const updatedStaffsIndexesCopy = updatedStaffsIndexes;
      updatedStaffsIndexesCopy.push(updatedStaffIndex);
      setUpdatedStaffsIndexes(updatedStaffsIndexesCopy);
    }
    setUpdatedStaffIndex(undefined);
    setUpdatedStaff(undefined);
    setShowAnotherModal(undefined);
  }, [
    addedStaffs,
    existingStaffs,
    showAnotherModal,
    updatedStaff,
    updatedStaffIndex,
    updatedStaffsIndexes,
  ]);

  const updateModal = useMemo(() => {
    return (
      <Modal
        onClose={() => {
          setShowAnotherModal(undefined);
        }}
        title="スタッフ情報修正"
      >
        <>
          <InputLabel>名前</InputLabel>
          <TextField
            onChange={(event) => {
              onUpdated("name", event.target.value);
            }}
            defaultValue={updatedStaff ? updatedStaff.name : ""}
            error={updatedStaffError.name}
            helperText={
              updatedStaffError.name ? "使用できない文字が含まれています" : ""
            }
          />
          <InputLabel>ふりがな</InputLabel>
          <TextField
            onChange={(event) => {
              onUpdated("phonetic", event.target.value);
            }}
            defaultValue={updatedStaff ? updatedStaff.phonetic : ""}
            error={updatedStaffError.phonetic}
            helperText={
              updatedStaffError.phonetic ? "ひらがなのみ使用できます" : ""
            }
          />
          <Button
            label="反映"
            onClick={decideUpdate}
            buttonType="forward"
            disabled={updatedStaffError.name || updatedStaffError.phonetic}
          />
        </>
      </Modal>
    );
  }, [
    decideUpdate,
    onUpdated,
    updatedStaff,
    updatedStaffError.name,
    updatedStaffError.phonetic,
  ]);

  const components = useMemo(() => {
    const body = (
      <>
        <div className="left-area">
          {leftBox}
          <div className="messages">{errorMessages}</div>
        </div>
        <div className="right-area">
          {rightBox}
          <div className="buttons">
            <div className="button">
              <Button
                width={130}
                label="スタッフ追加"
                onClick={() => {
                  setShowAnotherModal("setPhonetic");
                }}
                buttonType="powerful-red"
                disabled={!addButtonActive}
              />
            </div>
            <div className="button">
              <Button width={130} label="保存" onClick={save} buttonType="powerful-red" />
            </div>
          </div>
        </div>
      </>
    );

    const resultBar = (
      <ResultBar
        setSubmitError={setSaveError}
        submitError={saveError}
        setSubmitSucceeded={setSaveSuccess}
        submitSucceeded={saveSuccess}
        succeedMsg="正常に保存されました"
        failedMsg="保存できませんでした"
      />
    );
    return props.device === "PC" ? (
      <>
        <Modal
          onClose={props.onClose}
          title="スタッフ設定"
          width={props.device === "PC" ? 1000 : 330}
          maxHeight={props.device === "PC" ? 800 : 500}
        >
          <>
            <SetupStaffsStylePC>{body}</SetupStaffsStylePC>
            {showAnotherModal === "setPhonetic" ? setPhoneticModal : <></>}
            {showAnotherModal === "updateAdded" ||
            showAnotherModal === "updateExisting" ? (
              updateModal
            ) : (
              <></>
            )}
          </>
        </Modal>
        {loading ? <LoadingIcon /> : <></>}
        {resultBar}
      </>
    ) : (
      <>
        <Modal onClose={props.onClose} title="スタッフ設定">
          <>
            <SetupStaffsStyleMobile>{body}</SetupStaffsStyleMobile>
            {showAnotherModal === "setPhonetic" ? setPhoneticModal : <></>}
            {showAnotherModal === "updateAdded" ||
            showAnotherModal === "updateExisting" ? (
              updateModal
            ) : (
              <></>
            )}
          </>
        </Modal>
        {loading ? <LoadingIcon /> : <></>}
        {resultBar}
      </>
    );
  }, [
    addButtonActive,
    errorMessages,
    leftBox,
    loading,
    props.device,
    props.onClose,
    rightBox,
    save,
    saveError,
    saveSuccess,
    setPhoneticModal,
    showAnotherModal,
    updateModal,
  ]);

  return components;
};

export default SetupStaffs;

const SetupStaffsStyleMobile = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  .left-area {
    width: 150px;
    .messages {
      font-size: 12px;
      .helper-message {
        color: black;
        margin: 5px auto 5px 0;
      }
      .error-message {
        color: red;
        margin: 5px auto 5px 0;
      }
    }
  }
  .right-area {
    width: 160px;
    .buttons {
      margin: 10px auto 10px auto;
      .button {
        margin: 5px auto 5px auto;
      }
    }
    .right-box {
      width: 150px;
      .staffs-display {
        height: 250px;
        width: 160px;
        margin: auto;
        overflow-y: scroll;
        .staff-parallelogram {
          margin: 20px auto 20px auto;
          font-size: 10px;
          .existing-staff {
            width: 100px; 
            height: 40px;
            background-color: black;
            display: flex;
            align-items: center;
            justify-content: left;
            z-index: 1;
            color: white;
            cursor: pointer;
            gap: 10px;
            .existing-staff-property {
              width: 100px;
              text-align: left;
              padding-left: 0px;
              .existing-staff-name {
                width: 120px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .existing-staff-phonetic {
                width: 120px;
                font-size: 10px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
          .added-staff {
            width: 100px;
            height: 40px;
            background-color: red;
            display: flex;
            align-items: center;
            justify-content: left;
            z-index: 1;
            color: white;
            cursor: pointer;
            gap: 10px;
            .added-staff-property {
              width: 100px;
              text-align: left;
              padding-left: 30px;
              .added-staff-name {
                width: 100px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .added-staff-phonetic {
                width: 100px;
                font-size: 10px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
        }
      }
    }
  }

`;

const SetupStaffsStylePC = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  .left-area {
    width: 400px;
    .messages {
      font-size: 12px;
      .helper-message {
        color: black;
        margin: 5px auto 5px 0;
      }
      .error-message {
        color: #eb587f;
        margin: 5px auto 5px 0;
      }
    }
  }
  .right-area {
    width: 400px;
    .buttons {
      margin: 10px auto 10px auto;
      .button {
        margin: 10px auto 10px auto;
      }
    }
    .right-box {
      .staffs-display {
        height: 380px;
        width: 420px;
        overflow-y: scroll;
        .staff-parallelogram {
          margin: 20px auto 20px auto;
          .existing-staff {
            width: 380px;
            height: 50px;
            background-color: black;
            display: flex;
            align-items: center;
            justify-content: left;
            gap: 50px;
            z-index: 1;
            color: white;
            cursor: pointer;
            .existing-staff-property {
              width: 200px;
              text-align: left;
              padding-left: 30px;
              .existing-staff-name {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .existing-staff-phonetic {
                width: 200px;
                font-size: 12px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
          .added-staff {
            width: 380px;
            height: 50px;
            background-color: red;
            display: flex;
            align-items: center;
            justify-content: left;
            gap: 50px;
            z-index: 1;
            color: white;
            cursor: pointer;
            .added-staff-property {
              width: 200px;
              text-align: left;
              padding-left: 30px;
              .added-staff-name {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .added-staff-phonetic {
                width: 200px;
                font-size: 12px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
        }
      }
    }
  }
  .phonetic-input-wrapper {
    height: 250px;
    .phonetic-staff-name {
      color: black;
    }
  }
`;
